// File: frontend/src/pages/Settings/SettingsPage.jsx
// Main Settings page with different configuration sections

import { useState } from 'react';
import { 
  CogIcon, 
  BuildingOfficeIcon, 
  UserIcon, 
  BellIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

// Import setting components
import SystemSettings from '../../components/Settings/SystemSettings';
import UserSettings from '../../components/Settings/UserSettings';
import PipelineSettings from '../../components/Settings/PipelineSettings';
import NotificationSettings from '../../components/Settings/NotificationSettings';
import SecuritySettings from '../../components/Settings/SecuritySettings';
import ConfigurationExport from '../../components/Settings/ConfigurationExport';

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState('system');

  const settingsSections = [
    {
      id: 'system',
      name: 'Sistema',
      description: 'Configurações gerais do sistema',
      icon: CogIcon,
      component: SystemSettings
    },
    {
      id: 'company',
      name: 'Empresa',
      description: 'Informações da empresa',
      icon: BuildingOfficeIcon,
      component: SystemSettings,
      filter: 'company'
    },
    {
      id: 'user',
      name: 'Perfil Personal',
      description: 'Suas preferências pessoais',
      icon: UserIcon,
      component: UserSettings
    },
    {
      id: 'notifications',
      name: 'Notificações',
      description: 'Configurar alertas e notificações',
      icon: BellIcon,
      component: NotificationSettings
    },
    {
      id: 'pipeline',
      name: 'Pipeline de Vendas',
      description: 'Configurar etapas do processo comercial',
      icon: ChartBarIcon,
      component: PipelineSettings
    },
    {
      id: 'security',
      name: 'Segurança',
      description: 'Políticas de segurança e acesso',
      icon: ShieldCheckIcon,
      component: SecuritySettings
    },
    {
      id: 'export',
      name: 'Exportar/Importar',
      description: 'Exportar e importar configurações',
      icon: DocumentArrowDownIcon,
      component: ConfigurationExport
    }
  ];

  const activeSettingSection = settingsSections.find(section => section.id === activeSection);
  const ActiveComponent = activeSettingSection?.component;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-indigo-600" />
              <div className="ml-3">
                <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate sm:text-3xl">
                  Configurações
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Personalize e configure seu CRM conforme suas necessidades
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white dark:bg-gray-900 shadow-sm border-r border-gray-200 dark:border-gray-800">
          <nav className="mt-5 px-2 space-y-1">
            {settingsSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`${
                    isActive
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-500 text-indigo-700 dark:text-indigo-300'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                  } group w-full flex items-start px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-150`}
                >
                  <Icon
                    className={`${
                      isActive ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                    } flex-shrink-0 h-6 w-6`}
                  />
                  <div className="ml-3 text-left">
                    <p className="text-sm font-medium">{section.name}</p>
                    <p className={`text-xs ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {section.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="mb-6">
              <div className="flex items-center">
                {activeSettingSection && (
                  <>
                    <activeSettingSection.icon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                    <h2 className="ml-2 text-lg font-medium text-gray-900 dark:text-white">
                      {activeSettingSection.name}
                    </h2>
                  </>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-600">
                {activeSettingSection?.description}
              </p>
            </div>

            {/* Dynamic Content */}
            <div className="bg-white shadow rounded-lg">
              {ActiveComponent && (
                <ActiveComponent 
                  filter={activeSettingSection.filter}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;